import React, { useContext } from 'react';
import { Box, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faCalendar,
	faCalendarCheck,
	faCalendarXmark,
	faCashRegister,
	faChartPie,
	faCoins,
	faDownload,
	faHandHoldingDollar,
	faChartLine,
	faMapLocationDot,
	faMoneyBillTrendUp,
	faPiggyBank,
	faRoute,
	faSatelliteDish,
	faScaleBalanced,
	faUserCheck,
	faUsers,
	faUsersViewfinder,
	faWallet
} from '@fortawesome/free-solid-svg-icons';
import CallerSelector from '../../components/CallerSelector';
import SelectorClientsGestor from './selectores/selectoresCartera/SelectorClientsGestor';
import SelectorCreditsByGestor from './selectores/selectoresCartera/SelectorCreditsByGestor';
import SelectorCobroDiario from './selectores/selectoresCartera/SelectorCobroDiario';
import SelectorRecuperacion from './selectores/selectoresCartera/SelectorRecuperacion';
import SelectorDesembolsos from './selectores/selectoresCartera/SelectorDesembolsos';
import SelectorSaldosCartera from './selectores/selectoresCartera/SelectorSaldosCartera';
import SelectorCreditsByBarrio from './selectores/selectoresCartera/SelectorCreditsByBarrio';
import SelectorProyeccionIntereses from './selectores/selectoresCartera/SelectorProyeccionIntereses';
import SelectorRecuperacionEfectiva from './selectores/selectoresCartera/SelectorRecuperacionEfectiva';
import SelectorCreditosVencidos from './selectores/selectoresCartera/SelectorCreditosVencidos';
import SelectorAccountStatus from './selectores/selectoresCartera/SelectorAccountStatus';
import SelectorCierreDiario from './selectores/selectoresCartera/SelectorCierreDiario';
import SelectorEstadoResultado from './selectores/selectoresCartera/SelectorEstadoResultado';
import SelectorCreditsCancelled from './selectores/selectoresCartera/SelectorCreditsCancelled';
import SelectorVencimientosProyectados from './selectores/selectoresCartera/SelectorVencimientosProyectados';
import { isAccess } from '../../helpers/Helpers';
import { DataContext } from '../../context/DataContext';
import SelectorRutaGestor from './selectores/selectoresCartera/SelectorRutaGestor';
import SelectorRutaCobro from './selectores/selectoresCartera/SelectorRutaCobro';
import SelectorComportamientoPago from './selectores/selectoresCartera/SelectorComportamientoPago';
import SelectorCumplimientoVisitas from './selectores/selectoresCartera/SelectorCumplimientoVisitas';
import SelectorMoraPorZona from './selectores/selectoresCartera/SelectorMoraPorZona';
import SelectorGestoresEnVivo from './selectores/selectoresCartera/SelectorGestoresEnVivo';
import SelectorSinRiesgo from './selectores/selectoresCartera/SelectorSinRiesgo';

// Encabezado de seccion — ver design_handoff_crediapp_redesign/Reportes CrediApp.dc.html:
// icono-chip 34x34 radio 10px + titulo Poppins 600 17px + conteo en faint.
const SectionHeader = ({ icon, title, count, sx }) => (
	<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', ...sx }}>
		<Box
			sx={{
				width: 34,
				height: 34,
				borderRadius: '10px',
				backgroundColor: 'rgba(18,163,90,.13)',
				color: '#12a35a',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				flexShrink: 0
			}}
		>
			<FontAwesomeIcon icon={icon} style={{ width: 18, height: 18 }} />
		</Box>
		<Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: 17, color: 'text.primary' }}>
			{title}
		</Typography>
		<Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>{count}</Typography>
	</Box>
);

const Reports = () => {
	const { access } = useContext(DataContext);

	// Cada entrada se muestra solo si el rol tiene el permiso correspondiente,
	// igual que antes; el conteo del encabezado refleja los visibles.
	const reportes = [
		{ permiso: 'RCLIENTS', icon: faUsers, text: 'Reporte de clientes', modalTitle: 'Reporte de Clientes', selector: <SelectorClientsGestor /> },
		{ permiso: 'RCREDXGEST', icon: faHandHoldingDollar, text: 'Créditos por gestor', modalTitle: 'Créditos por Gestor', selector: <SelectorCreditsByGestor /> },
		{ permiso: 'RCOBDIARIO', icon: faCalendarCheck, text: 'Cobro diario', modalTitle: 'Cobro Diario', selector: <SelectorCobroDiario /> },
		{ permiso: 'RECUPERAC', icon: faUserCheck, text: 'Recuperación', modalTitle: 'Reporte de Recuperacion', selector: <SelectorRecuperacion isByClient={false} /> },
		{ permiso: 'RECUPERAC', icon: faUsersViewfinder, text: 'Recuperación por cliente', modalTitle: 'Reporte de Recuperacion por Cliente', selector: <SelectorRecuperacion isByClient={true} /> },
		{ permiso: 'RDESEMBOLSO', icon: faWallet, text: 'Desembolsos', modalTitle: 'Reporte de Desembolsos', selector: <SelectorDesembolsos /> },
		{ permiso: 'RSALCART', icon: faCoins, text: 'Saldos de cartera', modalTitle: 'Reporte Saldos Cartera', selector: <SelectorSaldosCartera /> },
		{ permiso: 'RCREDBARRIO', icon: faMapLocationDot, text: 'Créditos por barrio', modalTitle: 'Reporte Créditos por Barrio', selector: <SelectorCreditsByBarrio /> },
		{ permiso: 'RPROYINT', icon: faChartLine, text: 'Proyección de intereses', modalTitle: 'Proyección de Recuperación de Intereses', selector: <SelectorProyeccionIntereses /> },
		{ permiso: 'RRECEFEC', icon: faPiggyBank, text: 'Recuperación efectiva', modalTitle: 'Recuperación Efectiva de Intereses', selector: <SelectorRecuperacionEfectiva /> },
		{ permiso: 'RCREDVENC', icon: faCalendarXmark, text: 'Créditos vencidos', modalTitle: 'Reporte Créditos Vencidos', selector: <SelectorCreditosVencidos /> },
		{ permiso: 'RVENCPROY', icon: faCalendar, text: 'Vencimientos proyectados', modalTitle: 'Reporte Vencimientos proyectados', selector: <SelectorVencimientosProyectados /> },
		{ permiso: 'RCREDCANC', icon: faMoneyBillTrendUp, text: 'Créditos cancelados', modalTitle: 'Reporte Créditos Cancelados', selector: <SelectorCreditsCancelled /> },
		{ permiso: 'RACCOUNTSTS', icon: faScaleBalanced, text: 'Estado de cuenta', modalTitle: 'Reporte de Estado de Cuenta', selector: <SelectorAccountStatus /> },
		{ permiso: 'RBOXCLOSE', icon: faCashRegister, text: 'Cierre de caja', modalTitle: 'Reporte de Cierre de Caja', selector: <SelectorCierreDiario /> },
		{ permiso: 'RRESULTSTS', icon: faChartPie, text: 'Estado de resultado', modalTitle: 'Reporte Estado de Resultado', selector: <SelectorEstadoResultado /> },
		{ permiso: 'VER UBICACIONES', icon: faRoute, text: 'Recorrido del gestor', modalTitle: 'Recorrido del Gestor', selector: <SelectorRutaGestor /> },
		{ permiso: 'VER UBICACIONES', icon: faSatelliteDish, text: 'Gestores en vivo', modalTitle: 'Gestores en Vivo', selector: <SelectorGestoresEnVivo /> },
		{ permiso: 'RUTA COBRO', icon: faRoute, text: 'Ruta óptima de cobro', modalTitle: 'Ruta Óptima de Cobro', selector: <SelectorRutaCobro /> },
		{ permiso: 'RCOMPPAGO', icon: faUserCheck, text: 'Comportamiento de pago', modalTitle: 'Reporte de Comportamiento de Pago', selector: <SelectorComportamientoPago /> },
		{ permiso: 'RVISITAS', icon: faUsersViewfinder, text: 'Cumplimiento de visitas', modalTitle: 'Reporte de Cumplimiento de Visitas', selector: <SelectorCumplimientoVisitas /> },
		{ permiso: 'RMORAZONA', icon: faMapLocationDot, text: 'Mora por zona', modalTitle: 'Reporte de Mora por Zona', selector: <SelectorMoraPorZona /> },
		{ permiso: 'KONAMI VER', icon: faDownload, text: 'Reporte sin riesgo', modalTitle: 'Reporte Sin Riesgo', selector: <SelectorSinRiesgo /> }
	];

	const visibles = reportes.filter((r) => isAccess(access, r.permiso));

	return (
		<div>
			<SectionHeader
				icon={faWallet}
				title="Reportes de cartera"
				count={`${visibles.length} ${visibles.length === 1 ? 'reporte' : 'reportes'}`}
			/>

			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fill, 232px)',
					gap: '16px',
					justifyContent: 'center'
				}}
			>
				{visibles.map((r, index) => (
					<CallerSelector
						key={`${r.permiso}-${index}`}
						icon={r.icon}
						text={r.text}
						modalTitle={r.modalTitle}
					>
						{r.selector}
					</CallerSelector>
				))}
			</Box>
		</div>
	);
};

export default Reports;
